import { ClearSelectionIcon } from "../icons/questionnaireIcons";
import { CSS_CLASSES } from "../../constants/app";
import type {
  QuestionnaireAnswerValue,
  QuestionnaireQuestion,
  QuestionnaireSection
} from "../../types";
import { isAnswered, toArray, toNumberValue } from "./questionnaire.utils";

interface QuestionStepProps {
  section: QuestionnaireSection;
  question: QuestionnaireQuestion;
  answer: QuestionnaireAnswerValue;
  isActive: boolean;
  onSingleSelect: (value: string) => void;
  onClearSingleSelect: () => void;
  onMultiToggle: (value: string) => void;
  onOptionTextChange: (optionValue: string, textValue: string) => void;
  getOptionTextValue: (optionValue: string) => string;
  onTextChange: (value: string) => void;
  onNumberChange: (value: string) => void;
}

interface OptionPillProps {
  label: string;
  isSelected: boolean;
  isActive: boolean;
  index: number;
  onClick: () => void;
}

function OptionPill({
  label,
  isSelected,
  isActive,
  index,
  onClick
}: OptionPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ transitionDelay: isActive ? "0ms" : `${index * 60}ms` }}
      className={`min-h-11 px-3 py-2 rounded-full border text-sm transform-gpu transition-[opacity,transform,background-color,color,border-color,box-shadow] duration-200 ease-out hover:scale-[1.02] hover:shadow-md active:scale-[0.98] ${
        isActive ? "opacity-100 translate-y-0 scale-100" : "opacity-0 -translate-y-2 scale-95"
      } ${
        isSelected
          ? "border-rag-green bg-rag-green text-white"
          : "border-guardian-20 bg-white text-guardian-body hover:bg-guardian-10/60"
      }`}
    >
      {label}
    </button>
  );
}

function ClearSelectionControl({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Clear selection"
      className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-dashed border-guardian-20 bg-white px-3 py-1.5 text-sm font-semibold text-guardian-body transition-colors hover:border-rag-green hover:bg-guardian-10/60 hover:text-rag-green active:scale-[0.98]"
    >
      <ClearSelectionIcon />
      Clear selection
    </button>
  );
}

export default function QuestionStep({
  section,
  question,
  answer,
  isActive,
  onSingleSelect,
  onClearSingleSelect,
  onMultiToggle,
  onOptionTextChange,
  getOptionTextValue,
  onTextChange,
  onNumberChange
}: QuestionStepProps) {
  const selectedValues = toArray(answer);

  return (
    <>
      <p className="mb-1 text-xs uppercase tracking-wide text-guardian-header">{section.title}</p>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h4 className="text-lg font-semibold text-guardian-body">{question.label}</h4>
        {question.type === "single_select" && isAnswered(question, answer) && (
          <ClearSelectionControl onClick={onClearSingleSelect} />
        )}
      </div>
      {question.helperText && <p className={`mt-1 ${CSS_CLASSES.TEXT.MUTED}`}>{question.helperText}</p>}

      <div className="mt-5">
        {question.type === "single_select" && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {(question.options || []).map((option, index) => (
                <OptionPill
                  key={option.value}
                  label={option.label}
                  isSelected={answer === option.value}
                  isActive={isActive}
                  index={index}
                  onClick={() => onSingleSelect(option.value)}
                />
              ))}
            </div>
            {(question.options || [])
              .filter((option) => option.meta?.hasTextInput && answer === option.value)
              .map((option) => (
                <div key={`${option.value}-input`} className="max-w-lg">
                  <label className="mb-1 block text-xs font-medium text-guardian-header">
                    {option.meta?.textInputLabel || `${option.label} details`}
                  </label>
                  <input
                    type="text"
                    value={getOptionTextValue(option.value)}
                    onChange={(e) => onOptionTextChange(option.value, e.target.value)}
                    placeholder={
                      option.meta?.textInputPlaceholder || `Enter details for ${option.label}`
                    }
                    className={CSS_CLASSES.INPUTS.PATIENT}
                  />
                </div>
              ))}
          </div>
        )}

        {question.type === "multi_select" && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {(question.options || []).map((option, index) => (
                <OptionPill
                  key={option.value}
                  label={option.label}
                  isSelected={selectedValues.includes(option.value)}
                  isActive={isActive}
                  index={index}
                  onClick={() => onMultiToggle(option.value)}
                />
              ))}
            </div>
            {(question.options || [])
              .filter((option) => option.meta?.hasTextInput && selectedValues.includes(option.value))
              .map((option) => (
                <div key={`${option.value}-input`} className="max-w-lg">
                  <label className="mb-1 block text-xs font-medium text-guardian-header">
                    {option.meta?.textInputLabel || `${option.label} details`}
                  </label>
                  <input
                    type="text"
                    value={getOptionTextValue(option.value)}
                    onChange={(e) => onOptionTextChange(option.value, e.target.value)}
                    placeholder={
                      option.meta?.textInputPlaceholder || `Enter details for ${option.label}`
                    }
                    className={CSS_CLASSES.INPUTS.PATIENT}
                  />
                </div>
              ))}
          </div>
        )}

        {question.type === "text" && (
          <div className="relative">
            <input
              type="text"
              value={typeof answer === "string" ? answer : ""}
              onChange={(e) => onTextChange(e.target.value)}
              placeholder={question.placeholder || "Enter your answer"}
              className={`${CSS_CLASSES.INPUTS.PATIENT} ${
                question.unit ? "pr-16" : ""
              }`}
            />
            {question.unit && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-guardian-header">
                {question.unit}
              </span>
            )}
          </div>
        )}

        {question.type === "number" && (
          <div className="relative w-full sm:max-w-xs">
            <input
              type="number"
              min={question.unit === "years" ? 1 : undefined}
              step={question.unit === "years" ? 1 : undefined}
              value={toNumberValue(answer) ?? ""}
              onChange={(e) => {
                const rawValue = e.target.value;
                if (question.unit === "years") {
                  if (rawValue === "") {
                    onNumberChange(rawValue);
                    return;
                  }

                  if (!/^\d+$/.test(rawValue)) {
                    return;
                  }

                  const parsed = Number(rawValue);
                  if (parsed < 1) {
                    return;
                  }

                  onNumberChange(String(parsed));
                  return;
                }

                onNumberChange(rawValue);
              }}
              onKeyDown={(e) => {
                if (
                  question.unit === "years" &&
                  (e.key === "-" || e.key === "e" || e.key === "E" || e.key === ".")
                ) {
                  e.preventDefault();
                }
              }}
              placeholder={question.placeholder || "Enter value"}
              className={`${CSS_CLASSES.INPUTS.PATIENT} pr-16`}
            />
            {question.unit && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-guardian-header">
                {question.unit}
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );
}
